import { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useChatStore, ChatMessage, SuggestedAction } from '../../store/chatStore';
import { useEmailStore } from '../../store/emailStore';
import { chatApi } from '../../api/chat';
import { Sparkles, Globe, MessageSquare, Send, Trash2, X, User, Mail, FileText, Search, ChevronDown, ChevronRight, Maximize2, Minimize2, Grip, Calendar, Clipboard, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

const STORAGE_KEY = 'vetmail-chat-size';
const DEFAULT_WIDTH = 384;
const DEFAULT_HEIGHT = 560;
const MIN_WIDTH = 320;
const MIN_HEIGHT = 400;

type ChatSize = 'normal' | 'maximized' | 'minimized';

interface ChatDimensions {
  width: number;
  height: number;
  size: ChatSize;
}

function loadDimensions(): ChatDimensions {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      return {
        width: Math.min(parsed.width || DEFAULT_WIDTH, viewportWidth * 0.9),
        height: Math.min(parsed.height || DEFAULT_HEIGHT, viewportHeight * 0.9),
        size: parsed.size || 'normal',
      };
    }
  } catch {}
  return { width: DEFAULT_WIDTH, height: DEFAULT_HEIGHT, size: 'normal' };
}

function saveDimensions(dim: ChatDimensions) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(dim));
  } catch {}
}

export function ChatPanel() {
  const navigate = useNavigate();
  const {
    isOpen,
    setIsOpen,
    scope,
    activeCaseId,
    activeCaseTitle,
    history,
    isLoading,
    addMessage,
    clearHistory,
    setIsLoading,
  } = useChatStore();

  const { setSelectedEmail } = useEmailStore();

  const [inputValue, setInputValue] = useState('');
  const [dimensions, setDimensions] = useState<ChatDimensions>(() => loadDimensions());
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const streamingContentRef = useRef<string>('');
  const lastUpdateRef = useRef<number>(0);
  const panelRef = useRef<HTMLDivElement>(null);
  const isResizingRef = useRef(false);
  const resizeStartRef = useRef({ x: 0, y: 0, width: 0, height: 0 });

  useEffect(() => {
    if (isOpen && messagesContainerRef.current) {
      messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
    }
  }, [history, isOpen, isLoading]);

  useEffect(() => {
    saveDimensions(dimensions);
  }, [dimensions]);

  const handleResizeStart = useCallback((e: React.MouseEvent) => {
    if (dimensions.size !== 'normal') return;
    e.preventDefault();
    isResizingRef.current = true;
    resizeStartRef.current = {
      x: e.clientX,
      y: e.clientY,
      width: dimensions.width,
      height: dimensions.height,
    };
    document.addEventListener('mousemove', handleResizeMove);
    document.addEventListener('mouseup', handleResizeEnd);
  }, [dimensions]);

  const handleResizeMove = useCallback((e: MouseEvent) => {
    if (!isResizingRef.current) return;
    const deltaX = resizeStartRef.current.x - e.clientX;
    const deltaY = resizeStartRef.current.y - e.clientY;
    const newWidth = Math.max(MIN_WIDTH, Math.min(resizeStartRef.current.width + deltaX, window.innerWidth * 0.9));
    const newHeight = Math.max(MIN_HEIGHT, Math.min(resizeStartRef.current.height + deltaY, window.innerHeight * 0.9));
    setDimensions(prev => ({ ...prev, width: newWidth, height: newHeight }));
  }, []);

  const handleResizeEnd = useCallback(() => {
    isResizingRef.current = false;
    document.removeEventListener('mousemove', handleResizeMove);
    document.removeEventListener('mouseup', handleResizeEnd);
  }, []);

  const handleSizeToggle = useCallback(() => {
    setDimensions(prev => {
      if (prev.size === 'normal') {
        return { ...prev, size: 'maximized' };
      } else if (prev.size === 'maximized') {
        return { ...prev, size: 'minimized' };
      } else {
        return { ...prev, size: 'normal' };
      }
    });
  }, []);

  useEffect(() => {
    return () => {
      document.removeEventListener('mousemove', handleResizeMove);
      document.removeEventListener('mouseup', handleResizeEnd);
    };
  }, [handleResizeMove, handleResizeEnd]);

  const handleEmailClick = async (emailId: string) => {
    try {
      const response = await fetch(`/api/v1/emails/${emailId}`);
      if (!response.ok) throw new Error('Email non trovata');
      const emailDetail = await response.json();
      
      if (emailDetail.case_id) {
        navigate(`/cases/${emailDetail.case_id}`);
        setIsOpen(false);
      } else {
        console.warn('Email senza case associato');
      }
    } catch (error) {
      console.error('Errore apertura email:', error);
    }
  };

  const handleSend = async (overrideMessage?: string) => {
    const message = (overrideMessage ?? inputValue).trim();
    if (!message || isLoading) return;

    const userMessage: ChatMessage = { role: 'user', content: message };
    const placeholderMessage: ChatMessage = { role: 'assistant', content: '' };
    
    // Aggiungi entrambi i messaggi in una volta
    useChatStore.setState((state) => ({
      history: [...state.history, userMessage, placeholderMessage],
    }));
    
    setInputValue('');
    setIsLoading(true);

    const assistantIndex = useChatStore.getState().history.length - 1;
    streamingContentRef.current = '';
    lastUpdateRef.current = 0;

    try {
      const response = await chatApi.stream(
        {
          message,
          scope,
          case_id: activeCaseId ?? undefined,
          history: history.map((m) => ({ role: m.role, content: m.content })),
        },
        (chunk) => {
          if (chunk.chunk) {
            streamingContentRef.current += chunk.chunk;
            const now = Date.now();
            if (now - lastUpdateRef.current > 50) {
              lastUpdateRef.current = now;
              useChatStore.setState((state) => ({
                history: state.history.map((m, i) =>
                  i === assistantIndex ? { ...m, content: streamingContentRef.current } : m
                ),
              }));
            }
          }
        }
      );

      // Final update with thinking, sources and suggestions
      useChatStore.setState((state) => ({
        history: state.history.map((m, i) =>
          i === assistantIndex
            ? {
                role: 'assistant',
                content: response.response,
                thinking: response.thinking,
                source_email_ids: response.source_email_ids,
                suggested_actions: response.suggested_actions,
              }
            : m
        ),
      }));
    } catch (error) {
      useChatStore.setState((state) => ({
        history: state.history.map((m, i) =>
          i === assistantIndex
            ? {
                role: 'assistant',
                content: 'Errore nella comunicazione con il server. Riprova.',
              }
            : m
        ),
      }));
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  if (!isOpen) return null;

  const scopeLabel = scope === 'thread'
    ? (activeCaseTitle ? activeCaseTitle : 'Caso corrente')
    : 'Globale';
  const ScopeIcon = scope === 'thread' ? MessageSquare : Globe;

  const getSizeIcon = () => {
    if (dimensions.size === 'normal') return <Maximize2 className="size-3.5" />;
    if (dimensions.size === 'maximized') return <Minimize2 className="size-3.5" />;
    return <Maximize2 className="size-3.5" />;
  };

  const getSizeTitle = () => {
    if (dimensions.size === 'normal') return 'Espandi';
    if (dimensions.size === 'maximized') return 'Riduci';
    return 'Espandi';
  };

  const panelStyle = (() => {
    if (dimensions.size === 'maximized') {
      return {
        width: '90vw',
        height: '90vh',
        bottom: '5vh',
        right: '5vw',
      };
    }
    if (dimensions.size === 'minimized') {
      return {
        width: DEFAULT_WIDTH,
        height: '48px',
      };
    }
    return {
      width: dimensions.width,
      height: dimensions.height,
    };
  })();

  return (
    <div
      ref={panelRef}
      data-testid="chat-panel"
      style={panelStyle}
      className={`mail-chat-window fixed bottom-24 right-6 bg-card border border-border rounded-xl shadow-popover flex flex-col z-40 ${
        dimensions.size === 'minimized' ? 'h-12' : ''
      }`}
    >
      {/* Resize handle (only when normal) */}
      {dimensions.size === 'normal' && (
        <div
          onMouseDown={handleResizeStart}
          className="absolute top-0 left-0 w-4 h-4 cursor-nw-resize z-50 opacity-0 hover:opacity-100 transition-opacity"
          title="Ridimensiona"
        >
          <Grip className="size-3 text-muted-foreground rotate-45" />
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 bg-primary text-primary-foreground rounded-t-xl">
        <div>
          <h2 className="text-sm font-semibold flex items-center gap-1.5">
            <Sparkles className="size-4" />
            VetMail AI Chat
          </h2>
          {dimensions.size !== 'minimized' && (
            <p className="text-xs text-white/70 flex items-center gap-1">
              <ScopeIcon className="size-3" />
              {scopeLabel}
            </p>
          )}
        </div>
        <div className="flex items-center gap-1">
          {history.length > 0 && dimensions.size !== 'minimized' && (
            <Button
              variant="ghost"
              size="icon"
              onClick={clearHistory}
              title="Pulisci chat"
              className="size-7 text-white/70 hover:text-white hover:bg-white/10"
            >
              <Trash2 className="size-3.5" />
            </Button>
          )}
          <Button
            variant="ghost"
            size="icon"
            onClick={handleSizeToggle}
            title={getSizeTitle()}
            className="size-7 text-white/70 hover:text-white hover:bg-white/10"
          >
            {getSizeIcon()}
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setIsOpen(false)}
            title="Chiudi chat"
            className="size-7 text-white/70 hover:text-white hover:bg-white/10"
          >
            <X className="size-4" />
          </Button>
        </div>
      </div>

      {/* Messages - hidden when minimized */}
      {dimensions.size !== 'minimized' && (
        <div
          ref={messagesContainerRef}
          className="flex-1 px-4 py-3 overflow-y-auto"
        >
          {history.length === 0 && (
            <div className="flex flex-col items-center justify-center h-full text-center text-muted-foreground">
              <Sparkles className="size-10 mb-3 text-muted-foreground/40" />
              <p className="text-sm font-medium text-foreground/70">Ciao! Sono il tuo assistente AI.</p>
              <p className="text-xs text-muted-foreground mt-1 mb-4">
                {scope === 'thread'
                  ? 'Chiedimi informazioni su questo caso.'
                  : 'Chiedimi qualsiasi cosa sulle email in triage.'}
              </p>
              <InitialSuggestions scope={scope} onSuggestionClick={(text) => handleSend(text)} />
            </div>
          )}

          <div className="flex flex-col gap-3">
            {history.map((msg, index) => (
              <MessageBubble
                key={index}
                message={msg}
                isLast={index === history.length - 1}
                scope={scope}
                onEmailClick={handleEmailClick}
                onSuggestionClick={(text) => handleSend(text)}
                onActionClick={(action) => {
                  if (action.type === 'action' && action.action === 'open_email' && action.email_id) {
                    handleEmailClick(action.email_id);
                  }
                }}
              />
            ))}
          </div>

          {isLoading && (
            <div className="flex items-start gap-2 mt-3">
              <div className="w-7 h-7 bg-primary/10 rounded-full flex items-center justify-center shrink-0">
                <Sparkles className="size-3.5 text-primary" />
              </div>
              <div className="bg-muted rounded-2xl rounded-tl-sm px-3 py-2">
                <TypingIndicator />
              </div>
            </div>
          )}
        </div>
      )}

      {/* Input - hidden when minimized */}
      {dimensions.size !== 'minimized' && (
        <div className="px-3 pb-3 pt-2 border-t border-border">
          <div className="flex items-end gap-2">
            <Textarea
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Scrivi un messaggio..."
              disabled={isLoading}
              rows={2}
              className="flex-1 resize-none text-sm"
            />
            <Button
              onClick={() => handleSend()}
              disabled={isLoading || !inputValue.trim()}
              size="icon"
              className="size-9 shrink-0"
              title="Invia"
            >
              <Send className="size-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Sub-components ────────────────────────────────────────────────

interface MessageBubbleProps {
  message: ChatMessage;
  isLast: boolean;
  scope: 'global' | 'thread';
  onEmailClick?: (emailId: string) => void;
  onSuggestionClick?: (text: string) => void;
  onActionClick?: (action: SuggestedAction) => void;
}

function MessageBubble({ message, isLast, scope, onEmailClick, onSuggestionClick, onActionClick }: MessageBubbleProps) {
  const isUser = message.role === 'user';
  const [showThinking, setShowThinking] = useState(false);

  const suggestions = !isUser && isLast ? getSuggestions(message, scope) : [];

  return (
    <div className={`flex items-start gap-2 ${isUser ? 'flex-row-reverse' : ''}`}>
      <div
        className={`w-7 h-7 rounded-full flex items-center justify-center text-sm shrink-0 ${
          isUser ? 'bg-primary text-white' : 'bg-primary/10'
        }`}
      >
        {isUser ? <User className="size-3.5" /> : <Sparkles className="size-3.5 text-primary" />}
      </div>
      <div className="flex flex-col gap-2 max-w-[80%]">
        {message.thinking && (
          <button
            onClick={() => setShowThinking(!showThinking)}
            className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
          >
            {showThinking ? <ChevronDown className="size-3" /> : <ChevronRight className="size-3" />}
            Reasoning
          </button>
        )}
        {showThinking && message.thinking && (
          <div className="px-2 py-1 text-xs text-muted-foreground bg-background/50 rounded border border-border/50 italic">
            {message.thinking}
          </div>
        )}
        <div
          className={`px-3 py-2 rounded-2xl text-sm leading-relaxed ${
            isUser
              ? 'bg-primary text-white rounded-tr-sm'
              : 'bg-muted text-foreground rounded-tl-sm'
          }`}
        >
          {isUser ? (
            <p className="whitespace-pre-wrap">{message.content}</p>
          ) : (
            <div className="prose prose-sm prose-invert max-w-none [&_p]:my-1 [&_ul]:my-1 [&_ol]:my-1 [&_li]:my-0.5 [&_code]:text-xs [&_pre]:bg-background/50 [&_pre]:p-2 [&_pre]:rounded [&_a]:text-primary [&_a]:underline">
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                components={{
                  a: ({ href, children }) => {
                    if (href?.startsWith('mailto:')) {
                      return <span className="text-primary/70">{children}</span>;
                    }
                    return <a href={href} target="_blank" rel="noopener noreferrer">{children}</a>;
                  }
                }}
              >
                {message.content}
              </ReactMarkdown>
            </div>
          )}
          {message.source_email_ids && message.source_email_ids.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-2 pt-2 border-t border-border/50">
              {message.source_email_ids.slice(0, 3).map((emailId) => (
                <button
                  key={emailId}
                  onClick={() => onEmailClick?.(emailId)}
                  className="inline-flex items-center gap-1 px-2 py-0.5 text-xs bg-primary/10 hover:bg-primary/20 text-primary rounded transition-colors"
                >
                  <Mail className="size-3" />
                  Apri
                </button>
              ))}
              {message.source_email_ids.length > 3 && (
                <span className="text-xs text-muted-foreground">
                  +{message.source_email_ids.length - 3} altre
                </span>
              )}
            </div>
          )}
        </div>
        {suggestions.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {suggestions.map((s, idx) => (
              <button
                key={idx}
                onClick={() => {
                  if (s.type === 'prompt' && s.text) {
                    onSuggestionClick?.(s.text);
                  } else if (s.type === 'action') {
                    onActionClick?.({
                      label: s.label,
                      type: s.type,
                      action: s.action,
                      email_id: s.email_id,
                    });
                  }
                }}
                className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-background hover:bg-muted border border-border rounded transition-colors"
              >
                {s.icon}
                {s.label}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function getSuggestions(message: ChatMessage, scope: 'global' | 'thread') {
  const suggestions: Array<{ label: string; text?: string; icon: React.ReactNode; type: 'prompt' | 'action'; action?: string; email_id?: string }> = [];

  // Priority: LLM-generated suggestions
  if (message.suggested_actions && message.suggested_actions.length > 0) {
    for (const action of message.suggested_actions) {
      const icon = getIconForAction(action);
      suggestions.push({
        label: action.label,
        text: action.prompt,
        icon,
        type: action.type,
        action: action.action,
        email_id: action.email_id,
      });
    }
    return suggestions;
  }

  // Fallback: pattern matching rules
  const content = message.content.toLowerCase();

  if (content.includes('appuntamento') || content.includes('calendario')) {
    suggestions.push({
      label: 'Vedi calendario',
      text: 'Quali appuntamenti sono previsti oggi?',
      icon: <Calendar className="size-3" />,
      type: 'prompt',
    });
  }

  if (content.includes('referto') || content.includes('esami')) {
    suggestions.push({
      label: 'Dettagli referto',
      text: 'Mostra i dettagli del referto menzionato.',
      icon: <Clipboard className="size-3" />,
      type: 'prompt',
    });
  }

  if (content.includes('urgente') || content.includes('priorità')) {
    suggestions.push({
      label: 'Email urgenti',
      text: 'Quali altre email urgenti ci sono?',
      icon: <AlertCircle className="size-3" />,
      type: 'prompt',
    });
  }

  if (content.match(/dr\.?\s+\w+/i) || content.includes('veterinario')) {
    suggestions.push({
      label: 'Info veterinario',
      text: 'Informazioni sul veterinario menzionato.',
      icon: <User className="size-3" />,
      type: 'prompt',
    });
  }

  // Scope-based defaults
  if (scope === 'thread' && suggestions.length < 2) {
    suggestions.push({
      label: 'Riassumi caso',
      text: 'Riassumi brevemente il contenuto di questo caso, evidenziando i punti principali.',
      icon: <FileText className="size-3" />,
      type: 'prompt',
    });
    if (!content.includes('bozza')) {
      suggestions.push({
        label: 'Crea bozza',
        text: 'Crea una bozza di risposta per questa email.',
        icon: <Send className="size-3" />,
        type: 'prompt',
      });
    }
  } else if (scope === 'global' && suggestions.length < 2) {
    suggestions.push({
      label: 'Cerca simili',
      text: 'Trova email con contenuto simile.',
      icon: <Search className="size-3" />,
      type: 'prompt',
    });
  }

  // Source emails
  if (message.source_email_ids && message.source_email_ids.length > 0 && suggestions.length < 3) {
    suggestions.push({
      label: 'Analizza email',
      text: 'Analizza in dettaglio le email citate e spiega i punti chiave.',
      icon: <Mail className="size-3" />,
      type: 'prompt',
    });
  }

  return suggestions.slice(0, 3);
}

function getIconForAction(action: SuggestedAction): React.ReactNode {
  const label = action.label.toLowerCase();
  if (label.includes('email') || label.includes('apri')) return <Mail className="size-3" />;
  if (label.includes('calendario') || label.includes('appuntamento')) return <Calendar className="size-3" />;
  if (label.includes('bozza') || label.includes('risposta')) return <Send className="size-3" />;
  if (label.includes('riassumi') || label.includes('riassunto')) return <FileText className="size-3" />;
  if (label.includes('cerca') || label.includes('simili')) return <Search className="size-3" />;
  if (label.includes('referto')) return <Clipboard className="size-3" />;
  if (label.includes('urgente')) return <AlertCircle className="size-3" />;
  return <Sparkles className="size-3" />;
}

function InitialSuggestions({ scope, onSuggestionClick }: { scope: 'global' | 'thread'; onSuggestionClick: (text: string) => void }) {
  const suggestions = scope === 'thread' ? [
    { label: 'Riassumi il caso', text: 'Riassumi brevemente questo caso clinico.', icon: <FileText className="size-3" /> },
    { label: 'Email del caso', text: 'Quali email sono associate a questo caso?', icon: <Mail className="size-3" /> },
    { label: 'Crea bozza risposta', text: 'Proponi una bozza di risposta per questo caso.', icon: <Send className="size-3" /> },
  ] : [
    { label: 'Email urgenti', text: 'Quali email urgenti sono in triage?', icon: <AlertCircle className="size-3" /> },
    { label: 'Appuntamenti oggi', text: 'Quali appuntamenti sono previsti oggi?', icon: <Calendar className="size-3" /> },
    { label: 'Cerca per paziente', text: 'Cerca informazioni su un paziente specifico.', icon: <Search className="size-3" /> },
    { label: 'Email non assegnate', text: 'Quante email non hanno veterinario assegnato?', icon: <Mail className="size-3" /> },
  ];

  return (
    <div className="flex flex-wrap gap-2 justify-center">
      {suggestions.map((s, idx) => (
        <button
          key={idx}
          onClick={() => onSuggestionClick(s.text)}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs bg-primary/10 hover:bg-primary/20 text-primary rounded-full transition-colors"
        >
          {s.icon}
          {s.label}
        </button>
      ))}
    </div>
  );
}

function TypingIndicator() {
  return (
    <div className="flex items-center gap-1 py-1">
      <span className="size-2 bg-muted-foreground/40 rounded-full animate-bounce [animation-delay:0ms]" />
      <span className="size-2 bg-muted-foreground/40 rounded-full animate-bounce [animation-delay:150ms]" />
      <span className="size-2 bg-muted-foreground/40 rounded-full animate-bounce [animation-delay:300ms]" />
    </div>
  );
}
